import { APIResponseType } from 'app/apiService/types';
import apiService from '../../../apiService';

// Types for Import/Export functionality
export interface ImportExportEntity {
  entity: string;
  filters: EntityFilter[];
}

export interface EntityFilter {
  field: string;
  type: string;
  label: string;
  placeholder: string;
  defaultValue?: any;
}

export interface EntityField {
  name: string;
  type: string;
  required: boolean;
  description?: string;
  options?: string[];
  validation_rules?: any;
}

export interface ImportExportEntitiesResponse {
  body: ImportExportEntity[];
  message: string;
  statusCode: number;
}

export interface EntityFieldsResponse {
  fields: EntityField[];
  entity_type: string;
  total_fields: number;
}

export interface CompareEntitiesResponse {
  new_entities: any[];
  existing_entities: any[];
  conflicts: any[];
  summary: {
    total_new: number;
    total_existing: number;
    total_conflicts: number;
  };
}

export interface ExportDataResponse {
  download_url: string;
  file_size: number;
  total_records: number;
  format: string;
}

export interface ImportDataResponse {
  success: boolean;
  imported_count: number;
  failed_count: number;
  errors: any[];
  warnings: any[];
}

export interface JobStatusResponse {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  result?: any;
  error?: string;
}

class ImportExportApiService {
  static ENDPOINT = 'api/v2/import-export';

  // Get available entities for import/export
  static async getEntities(): Promise<APIResponseType<ImportExportEntitiesResponse>> {
    try {
      
      const url = `${this.ENDPOINT}/entities`;
      
      const response = await apiService.get<ImportExportEntitiesResponse>(url);
      
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get fields for a specific entity type
  static async getEntityFields(params: {
    entity_type: string;
  }): Promise<APIResponseType<EntityFieldsResponse>> {
    try {
      
      const response = await apiService.get<EntityFieldsResponse>(
        `${this.ENDPOINT}/entities/${params.entity_type}/fields`
      );
      
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Export data for a specific entity type
  static async exportData(entityType: string, params?: {
    format?: 'zip' | 'csv' | 'json';
    dealer?: string;
    filters?: any; // DataGridMAIQ style filter object
    fields?: string[];
    limit?: string | number;
    skip?: number;
  }): Promise<APIResponseType<ExportDataResponse>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.dealer) queryParams.append('dealer', params.dealer);
      if (params?.skip) queryParams.append('skip', params.skip.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      
      // Handle DataGridMAIQ style filters
      if (params?.filters) {
        const filtersString = JSON.stringify(params.filters);
        queryParams.append('filters', filtersString);
      }
      
      // Handle selected fields
      if (params?.fields && params.fields.length > 0) {
        queryParams.append('fields', params.fields.join(','));
      }

      const format = params?.format || 'zip';
      const url = `${this.ENDPOINT}/export/${entityType}/${format}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await apiService.get(url, {
        responseType: 'blob' // Handle as blob for file downloads
      });
     
      
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Import data from file (ZIP/CSV/JSON)
  static async importData(entityType: string, formData: FormData, params?: {
    dealer?: string;
    validate_only?: boolean;
    update_existing?: boolean;
  }): Promise<APIResponseType<ImportDataResponse>> {
    try {
      
      const queryParams = new URLSearchParams();
      if (params?.dealer) queryParams.append('dealer', params.dealer);
      if (params?.validate_only) queryParams.append('validate_only', params.validate_only.toString());
      if (params?.update_existing) queryParams.append('update_existing', params.update_existing.toString());

      const url = `${this.ENDPOINT}/import/${entityType}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await apiService.post<ImportDataResponse>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Import data from JSON payload (for edited data)
  static async importDataFromJson(entityType: string, jsonData: any[], params?: {
    dealer?: string;
    validate_only?: boolean;
    update_existing?: boolean;
  }): Promise<APIResponseType<ImportDataResponse>> {
    try {
      
      const queryParams = new URLSearchParams();
      if (params?.dealer) queryParams.append('dealer', params.dealer);
      if (params?.validate_only) queryParams.append('validate_only', params.validate_only.toString());
      if (params?.update_existing) queryParams.append('update_existing', params.update_existing.toString());

      const url = `${this.ENDPOINT}/import/${entityType}`;
      
      const payload = {
        data: jsonData
      };
      
      const response = await apiService.post<ImportDataResponse>(url, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Compare entities from uploaded file with existing data
  static async compareEntities(entityType: string, formData: FormData, params?: {
    dealer?: string;
  }): Promise<APIResponseType<CompareEntitiesResponse>> {
    try {
      
      const queryParams = new URLSearchParams();
    //   if (params?.dealer) queryParams.append('dealer', params.dealer);

      const url = `${this.ENDPOINT}/compare/${entityType}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await apiService.post<CompareEntitiesResponse>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get import/export status
  static async getStatus(jobId: string): Promise<APIResponseType<JobStatusResponse>> {
    try {
      const response = await apiService.get(`${this.ENDPOINT}/status/${jobId}`);
      return response;
    } catch (error) {
      throw error;
    }
  }
}

export default ImportExportApiService;
